import { Loader } from '@/components/layout/loading';
import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { azureOwnCloudFlow } from './azureOwnCloudFlow';
import type { AzureOwnCloudState } from './azureOwnCloudState';

export function AzureOwnCloudOnboardingWizard({
  tenantId,
  onOnboardingFlowFinish,
}: {
  tenantId: string;
  onOnboardingFlowFinish: (cloudAccountId: string) => Promise<void>;
}) {
  const dal = useDAL();
  const { data: vaultAccounts, isLoading: isLoadingVaultAccounts } =
    dal.cloudAccounts.vault.useList();
  const { data: scanningAccounts, isLoading: isLoadingScanningAccounts } =
    dal.cloudAccounts.scanning.useList();
  const {
    data: azureBackupApplications,
    isLoading: isLoadingAzureBackupApplications,
  } = dal.cloudAccounts.azureApps.useApps('Source', tenantId);

  const { rightPanel } = useWorkspace();

  const onFinish = async (state: AzureOwnCloudState) => {
    const cloudAccountId =
      state.sourceConnectScope === 'subscription'
        ? state.sourceSubscriptionId
        : (state.sourceManagementGroupId ?? '');
    await onOnboardingFlowFinish(cloudAccountId);
    return true;
  };

  if (
    isLoadingVaultAccounts ||
    isLoadingScanningAccounts ||
    isLoadingAzureBackupApplications
  ) {
    return <Loader />;
  }

  const hasVaultAccounts = !!vaultAccounts?.accounts.length;
  const hasScanningAccounts = !!scanningAccounts?.accounts.length;
  const { managementAppId, backupAppId, keyVaultAccessAppId } =
    azureBackupApplications || {};
  const hasAzureBackupApplications = !!backupAppId && !!managementAppId;

  const stepIndex = hasVaultAccounts
    ? 3
    : hasScanningAccounts
      ? 2
      : hasAzureBackupApplications
        ? 1
        : 0;

  return (
    <Wizard<AzureOwnCloudState>
      hideCloseButton={false}
      onAbort={() => rightPanel.setIsOpen(false)}
      hideBackArrow={false}
      onFinish={onFinish}
      flow={[azureOwnCloudFlow]}
      initialStep={{ stepIndex, keepPreviousSteps: true }}
      initialState={{
        onboardingFlow: true,
        tenantId,
        managementApplicationName: 'EonBackupApplication',
        regionName: 'eastus2',
        sourceConnectScope: 'subscription',
        enableScopeSelection: true,
        sourceConnectMethod: 'terraform',
        managementApplicationId: managementAppId || '',
        backupAppId,
        keyVaultApplicationId: keyVaultAccessAppId,
        sourceSubscriptionId: '',
        sourceManagementGroupId: '',
      }}
      title='Set up Eon on your own cloud'
      stepperLabels={[
        'Management access',
        'Scanning account setup',
        'Vault account setup',
        'Source connection',
      ]}
      storageKey={`setup-own-cloud-${tenantId}`}
    />
  );
}
